/**
 *
 */
import { Ajax } from '@shared/Ajax';

// openlayers
import WMTSCapabilities from 'ol/format/WMTSCapabilities';
import WMSCapabilities from 'ol/format/WMSCapabilities';


const wmtsParser = new WMTSCapabilities();
const wmsParser = new WMSCapabilities();


interface IWmsLayerDef {
    name: string;
    key: string;
    boundingBox: number[];
    projections: string[];
    isWGS84: boolean;
}

export interface IMapTilelayer {
    username: string;
    password: string
    authToken: string;
    isProxy: boolean;
    demUrl: string;
    id?: number;
    uuid?: string;
    fileUuid?: string; // geotiff source file
    type: 'STATIC_IMAGE' | 'XYZ_TILE' | 'OSM_TILE' | 'WMS_TILE' | 'WMTS_TILE';
    order?: number;
    name: string;
    bbox: number[];
    url: string;
    maxZoom?: number;
    readOnly?: boolean;
    wmsParams?: { [key: string]: string };
}

function fetchLayers(): JQueryPromise<IMapTilelayer[]> {
    return Ajax({
        url: `/api/1/map-baselayer/list`
    }).then(resp => {
        resp.baselayers.forEach(layer => {
            if(layer.wmsParams && layer.wmsParams.length) {
                layer.wmsParams = layer.wmsParams.reduce((obj, curr) => {
                    obj[curr.key] = curr.value;
                    return obj;
                }, {});

                delete layer.wmsParams.id;
                delete layer.wmsParams.projectId;
                delete layer.wmsParams.uuid;
                delete layer.wmsParams.name;
                delete layer.wmsParams.url;
                delete layer.wmsParams.maxZoom;
                delete layer.wmsParams.order;
                delete layer.wmsParams.type;
            } else {
                layer.wmsParams = {};
            }
        });

        const layersEditable = resp.baselayers.filter(l => !l.readOnly);
        const layersReadonly = resp.baselayers.filter(l => l.readOnly);

        return [
            ...layersReadonly.sort((a, b) => a.order - b.order),
            ...layersEditable.sort((a, b) => a.order - b.order)
        ];
    });
}

function saveOrUpdateLayer(layer: IMapTilelayer): JQueryPromise<IMapTilelayer> {
    if (layer.type === 'XYZ_TILE') {
        delete layer.wmsParams;
    }
    // if no authentication delete parameters
    if (!layer.username && !layer.password) {
        delete layer.username
        delete layer.password
    }
    delete layer.authToken

    const data: any = JSON.parse(JSON.stringify(layer));

    // pull wms params into root object
    if (layer?.wmsParams) {
        Object.keys(layer.wmsParams).forEach(key => {
            data[key] = layer.wmsParams[key];
        });
    }
    delete data.wmsParams;
    delete data.crossOrigin;
    delete data.style;

    if (layer.uuid) {
        return Ajax({
            url: `/api/1/map-baselayer/update/` + layer.uuid,
            method: 'post',
            data
        }).then(resp => {
            return layer;
        });
    }

    return Ajax({
        url: `/api/1/map-baselayer/create`,
        method: 'post',
        data
    }).then(resp => {
        return resp.createdLayer;
    });
}

function deleteLayer(layerUuid: string): JQueryPromise<void> {
    return Ajax({
        url: `/api/1/map-baselayer/delete/` + layerUuid,
        method: 'post'
    });
}

function saveOrder(order: string[], startIndex: number = 0): JQueryPromise<void> {
    return Ajax({
        url: `/api/1/map-baselayer/set-order`,
        method: 'post',
        data: {
            idOrder: order.map((id, i) => id + '_' + (i + startIndex))
        }
    });
}

function parseWmsWmtsCapabilities(xml: XMLDocument): {
    TYPE: 'WMS_TILE' | 'WMTS_TILE',
    LAYERS: IWmsLayerDef[],
    VERSION: string
} {
    try {
        let version = '1.3.0';
        let type: 'WMS_TILE' | 'WMTS_TILE' = 'WMS_TILE';
        try {
            version = $(xml).children().eq(0).attr('version');
        } catch {}

        xml.documentElement.getAttributeNames().forEach(attr => {
            const attrVal = xml.documentElement.getAttribute(attr)?.toLowerCase() ?? '';
            if (attrVal.includes('wmts')) {
                type = 'WMTS_TILE';
            }
        });

        const layerEls = xml.querySelectorAll('Layer');
        const layersSet = new Set<IWmsLayerDef>();
        const matrixSetProjectionMap = {}; // projection might be stored inside custom referenced set
        xml.querySelectorAll('TileMatrixSet').forEach(tms => {
            const idEl = tms.querySelector('Identifier');
            const projEl = tms.querySelector('SupportedCRS') || tms.querySelector('CRS');
            if (idEl && projEl && !isSupportedProjection([idEl.textContent])) {
                matrixSetProjectionMap[idEl.textContent.toLowerCase()] = projEl.textContent;
            }
        });
        console.log('matrixSetProjectionMap:', matrixSetProjectionMap);

        layerEls.forEach(layerEl => {
            const nameEl = layerEl.querySelector('Name') || layerEl.querySelector('Identifier');
            const key = nameEl.textContent;

            if (!Array.from(layersSet).map(l => l.key).includes(key)) {
                const titleEl = layerEl.querySelector('Title') || layerEl.querySelector('Title');
                if (nameEl && nameEl.parentNode === layerEl) {
                    // projections
                    let projEls = [...Array.from(layerEl.querySelectorAll('CRS')), ...Array.from(layerEl.querySelectorAll('SRS')), ...Array.from(layerEl.querySelectorAll('TileMatrixSet'))];
                    if (!projEls.length) {
                        projEls = [...Array.from(xml.querySelectorAll('CRS')), ...Array.from(xml.querySelectorAll('SupportedCRS')), ...Array.from(xml.querySelectorAll('SRS'))];
                    }
                    let projections = projEls.map(el => el.textContent.toUpperCase().replace('EPSG:', ''));
                    projections = projections.map((p, i) => {
                        if (matrixSetProjectionMap[p.toLowerCase()]) {
                            return matrixSetProjectionMap[p.toLowerCase()];
                        }
                        return p;
                    })

                    layersSet.add({
                        name: titleEl ? titleEl.textContent : nameEl.textContent,
                        key,
                        boundingBox: getWmsWmtsLayerBoundingBox(layerEl),
                        projections,
                        isWGS84: !projections.length || isSupportedProjection(projections)
                    });
                }
            }
        });

        return {
            TYPE: type,
            LAYERS: Array.from(layersSet),
            VERSION: version
        };
    } catch (e) {
        console.log('parseWmsCapabilities exception:', e);
        return {
            TYPE: 'WMS_TILE',
            LAYERS: [],
            VERSION: '1.3.0'
        };
    }
}

function getWmsWmtsLayerBoundingBox(layerEl: Element) {
    let boundingBox = null;

    // try <BoundingBox>
    const bbEl = layerEl.querySelector('BoundingBox[CRS="CRS:84"]') || layerEl.querySelector('BoundingBox[SRS="EPSG:4326"]');
    if (bbEl) {
        boundingBox = [bbEl.getAttribute('minx'), bbEl.getAttribute('miny'), bbEl.getAttribute('maxx'), bbEl.getAttribute('maxy')];
    }

    // try <ows:WGS84BoundingBox>
    if (!boundingBox) {
        const lowerCornerEl = layerEl.querySelector('LowerCorner');
        const upperCornerEl = layerEl.querySelector('UpperCorner');
        if (lowerCornerEl && upperCornerEl) {
            boundingBox = [...lowerCornerEl.textContent.split(' '), ...upperCornerEl.textContent.split(' ')];
        }
    }

    return boundingBox;
}

function checkCORS(url: string): JQueryPromise<{
    Status: number,
    'CORS-Enabled': boolean,
    'Infrakit-Allowed': boolean
}> {
    // tslint:disable-next-line
    return $.ajax({
        url: '/kuura/http-options?url=' + encodeURIComponent(url),
        method: 'GET'
    });
}

// trim and compress capabilities object because of database column size limit
const WmtsCapabilities = {
    strip: (capabilities: any, keepLayer: string) => {
        capabilities = JSON.parse(JSON.stringify(capabilities));

        // only save active layer
        capabilities.Contents.Layer = capabilities.Contents.Layer.filter(l => l.Identifier === keepLayer);

        // only save TileMatrixSet we are going to use in this layer
        if (capabilities.Contents.TileMatrixSet.length > 1) {
            const matrixSetProjectionMap = {}; // projection might be stored inside custom referenced set
            capabilities.Contents.TileMatrixSet.forEach(tms => matrixSetProjectionMap[tms.Identifier] = tms.SupportedCRS);

            // filter out unsupported projections
            capabilities.Contents.Layer[0].TileMatrixSetLink = capabilities.Contents.Layer[0].TileMatrixSetLink.filter(link => {
                const basicSupported = isSupportedProjection([link.TileMatrixSet]);
                if (!basicSupported && matrixSetProjectionMap[link.TileMatrixSet]) {
                    return isSupportedProjection([matrixSetProjectionMap[link.TileMatrixSet]]);
                }
                return basicSupported;
            });

            const tileMatrixSet /* <TileMatrixSet>EPSG:3879</TileMatrixSet> */ = capabilities.Contents.Layer[0].TileMatrixSetLink[0]?.TileMatrixSet;
            if (tileMatrixSet) {
                capabilities.Contents.TileMatrixSet = capabilities.Contents.TileMatrixSet.filter(l => l.Identifier === tileMatrixSet);
            }
            else {
                capabilities.Contents.TileMatrixSet = [capabilities.Contents.TileMatrixSet[0]];
            }
        }

        // strip layer limits
        delete capabilities.Contents.Layer[0].TileMatrixSetLink[0]?.TileMatrixSetLimits;

        // keep only tile resources, strip features
        if (capabilities.Contents.Layer[0].ResourceURL) {
            capabilities.Contents.Layer[0].ResourceURL = capabilities.Contents.Layer[0].ResourceURL.filter(l => {
                return l.resourceType == 'tile' || l.format === 'image/png' || l.format === 'image/jpeg';
            });
        }
        return capabilities;
    },
    compress: (capabilities: any) => {
        capabilities = JSON.parse(JSON.stringify(capabilities));

        capabilities.LayerCap = capabilities.Contents.Layer;
        delete capabilities.Contents.Layer;

        capabilities.TileMatrixSet = capabilities.Contents.TileMatrixSet;
        delete capabilities.Contents.TileMatrixSet;

        delete capabilities.Contents;

        capabilities.TileMatrixSet[0].TileMatrix = capabilities.TileMatrixSet[0].TileMatrix.map(level => {
            level.i = level.Identifier;
            delete level.Identifier;

            level.sc = level.ScaleDenominator;
            delete level.ScaleDenominator;

            level.tl = level.TopLeftCorner;
            delete level.TopLeftCorner;

            level.w = level.TileWidth;
            delete level.TileWidth;
            delete level.TileHeight;

            level.mw = level.MatrixWidth;
            delete level.MatrixWidth;
            delete level.MatrixHeight;

            return level;
        });

        return capabilities;
    },
    decompressUnparsed: (wmsParams: any) => {
        const capabilitiesCompressed: any = {};
        Object.keys(wmsParams).forEach(key => {
            try {
                capabilitiesCompressed[key] = JSON.parse(wmsParams[key]);
            } catch {
                capabilitiesCompressed[key] = wmsParams[key];
            }
        });
        return WmtsCapabilities.decompress(capabilitiesCompressed);
    },
    decompress: (capabilities: any) => {
        if (!capabilities) return capabilities;
        capabilities = JSON.parse(JSON.stringify(capabilities));

        if (!capabilities.Contents) capabilities.Contents = {};

        capabilities.Contents.Layer = capabilities.LayerCap;
        delete capabilities.LayerCap;

        capabilities.Contents.TileMatrixSet = capabilities.TileMatrixSet;
        delete capabilities.TileMatrixSet;

        capabilities.Contents.TileMatrixSet[0].TileMatrix = capabilities.Contents.TileMatrixSet[0].TileMatrix.map(level => {
            level.Identifier = level.i;
            delete level.i;

            level.ScaleDenominator = level.sc;
            delete level.sc;

            level.TopLeftCorner = level.tl;
            delete level.tl;

            level.TileWidth = level.w;
            level.TileHeight = level.w;
            delete level.w;

            level.MatrixWidth = level.mw;
            level.MatrixHeight = level.mw;
            delete level.mw;

            return level;
        });

        return capabilities;
    },
    stringify: (capabilities: any) => {
        if (!capabilities) return capabilities;
        capabilities = JSON.parse(JSON.stringify(capabilities));

        Object.keys(capabilities).forEach(key => {
            if (typeof capabilities[key] === 'object') {
                capabilities[key] = JSON.stringify(capabilities[key]);
            }
        });
        return capabilities;
    }
};

function isSupportedProjection(projections: string | string[]): boolean {
    const projectionsArray = Array.isArray(projections) ? projections : [projections];
    const combined = projectionsArray.map(p => p.toUpperCase().replace('EPSG:', '')).join(" ");
    return combined.includes('CRS:84')
        || combined.includes('4326')
        || combined.includes('3857')
        || combined.includes('3047')
        || combined.includes('WGS84')
        || combined.includes('GOOGLEMAPSCOMPATIBLE');
}

export const TileLayerService = {
    fetchLayers,
    saveOrUpdateLayer,
    deleteLayer,
    saveOrder,
    parseWmsWmtsCapabilities,
    checkCORS,
    WmtsCapabilities
};
